/* eslint-disable react/jsx-max-depth,react/no-danger */
import { LaunchpadAPI, NFTI } from '@tacans/shared-marketplace-layer';
import { GetServerSideProps } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import withUniverseVerification from 'src/HOCs/withUniverseVerification';
import PageMeta from 'src/components/organisms/page-meta';
import { PAGES_LOCALES } from 'src/constants/common.constants';
import { RouteE } from 'src/constants/pages.constants';
import { fetchCollectionsByIdsThunk } from 'src/features/common/collection/ducks/list/list.thunk';
import { ExploreContentLayout } from 'src/features/common/explore/components/explore-content-layout';
import { FilterButton } from 'src/features/common/explore/components/filter-button';
import { CollectionsContainer } from 'src/features/common/explore/containers/collections-container';
import { CollectionsSearchContainer } from 'src/features/common/explore/containers/collections-search-container';
import { ExploreFilterContainer } from 'src/features/common/explore/containers/explore-filter-container';
import { ExploreTopFiltersContainer } from 'src/features/common/explore/containers/explore-top-filters-container';
import { NftsExploreContainer } from 'src/features/common/explore/containers/nfts-explore-container';
import { actions as collectionActions } from 'src/features/common/explore/ducks/collections/collections.slice';
import { fetchCollectionsThunk } from 'src/features/common/explore/ducks/collections/collections.thunk';
import { INITIAL_FILTER_STATE } from 'src/features/common/explore/ducks/explore-nfts/explore-nfts.constants';
import { actions } from 'src/features/common/explore/ducks/explore-nfts/explore-nfts.slice';
import { fetchNftsThunk } from 'src/features/common/explore/ducks/explore-nfts/explore-nfts.thunk';
import { actions as launchpadsActions } from 'src/features/common/explore/ducks/launchpads/launchpads.slice';
import { useTotal as useTotalCollections } from 'src/features/common/explore/hooks/use-explore-collections';
import { useSetFilters as useSetLaunchpadFilters, useTotal as useTotalLaunchpads } from 'src/features/common/explore/hooks/use-explore-launchpads';
import { useClearExplore, useFilters, useIsFiltersApplied, useSetFilters, useTotal } from 'src/features/common/explore/hooks/use-explore-nfts';
import { mapLaunchpadsSorting, useCreateLaunchpadsFilterFieldsPayload } from 'src/features/common/explore/launchpads-api.utils';
import { ProfileLaunchpadsContainer } from 'src/features/common/launchpad/containers/profile-launchpads-container';
import { ProfileLaunchpadsSearchContainer } from 'src/features/common/launchpad/containers/profile-launchpads-search-container';
import { TabItemsWrapper } from 'src/features/common/profile/components/tab-items-wrapper';
import { ProfileDetailsContainer } from 'src/features/common/profile/containers/profile-details-container';
import { fetchPublicProfileThunk } from 'src/features/common/profile/ducks/public-profile/public-profile.thunk';
import { useProfile } from 'src/features/common/profile/hooks/profile.hooks';
import { usePublicProfile } from 'src/features/common/profile/hooks/public-profile.hooks';
import { useAvatarAssets } from 'src/features/common/profile/hooks/use-avatar-assets';
import { useIsSeller } from 'src/features/common/profile/hooks/use-is-seller';
import { EmptyListPlaceholder } from 'src/features/common/shared/components/empty-list-placeholder';
import { useUniverse } from 'src/features/common/universe/hooks/use-universe';
import { useUniversePermissions } from 'src/features/common/universe/hooks/use-universe-permissions';
import { fetchUsersByWalletsThunk } from 'src/features/common/user/ducks/user-list';
import { useDisplayName } from 'src/features/common/user/hooks/use-display-name.hooks';
import { ProfilePage as OProfilePage } from 'src/features/white-labels/ocean/profile/components/profile-page';
import { useGetUniverseTemplate } from 'src/hooks/use-get-universe-template';
import { useMeta } from 'src/hooks/use-meta';
import usePreventUrlChange from 'src/hooks/use-prevent-url-change';
import { wrapper } from 'src/store';
import { Button } from 'src/ui/common/atoms/button';
import { Spacing } from 'src/ui/common/atoms/spacing';
import { Typography } from 'src/ui/common/atoms/typography';
import { PageLayout } from 'src/ui/common/layouts/page-layout';
import { Visible } from 'src/ui/common/layouts/visible';
import { Tabs } from 'src/ui/common/molecules/tabs';
import { makeSSRApiRequest } from 'src/utils/api.utils';


const ProfilePage = () => {
    const router = useRouter();
    const [sideBarOpened, setSideBarOpened] = useState(true);
    const [selectedTabId, setSelectedTabId] = useState('items');

    const { t } = useTranslation(['components']);
    const setLaunchpadsFilter = useSetLaunchpadFilters();
    const total = useTotal();
    const isSeller = useIsSeller();
    const { isUniverseOwner, isUniverse } = useUniverse();
    const totalLaunchpads = useTotalLaunchpads();
    const totalCollections = useTotalCollections();
    const clearExplore = useClearExplore();
    const setFilters = useSetFilters();
    const { keywords } = useFilters();
    const isFiltersApplied = useIsFiltersApplied('profile');
    const profile = usePublicProfile();
    const avatarAssets = useAvatarAssets({ profile: profile! });
    const { projectName } = useUniverse();
    const loggedProfile = useProfile();
    const profileName = useDisplayName(profile);
    const { canCreate } = useUniversePermissions();

    const userLogin = loggedProfile?.user?.login;
    const isProfileOwner = userLogin && profile && profile.login === userLogin;
    const { isOceanTemplate } = useGetUniverseTemplate();

    const meta = useMeta('profile', {
        projectName,
        profileName,
    });

    const tabs = useMemo(() => {
        const itemsLabel = isProfileOwner ? 'Owned' : 'Items';
        const commonAccess = !isUniverse || isSeller || isUniverseOwner;
        const isLaunchpadVisible = commonAccess || !!totalLaunchpads;
        const isCollectionsVisible = commonAccess || !!totalCollections;

        const result = [{ tabId: 'items', tab: total ? `${itemsLabel} (${total})` : `${itemsLabel}` }];

        if (isCollectionsVisible) {
            result.push({
                tabId: 'collections',
                tab: totalCollections ? `Collections (${totalCollections})` : 'Collections',
            });
        }

        if (isLaunchpadVisible) {
            result.push({
                tabId: 'launchpads',
                tab: totalLaunchpads ? `Launchpads (${totalLaunchpads})` : 'Launchpads',
            });
        }
        return result;
    }, [isProfileOwner, total, totalCollections, totalLaunchpads, isUniverse, isUniverseOwner, isSeller]);

    const toggleSideBar = useCallback(() => setSideBarOpened((s) => !s), []);

    const clearFilters = useCallback(() => {
        setFilters({
            ...INITIAL_FILTER_STATE.filter,
            ownerIds: [profile?.id! as string],
        });
    }, [profile?.id, setFilters]);

    const emptyList = useMemo(() => {
        let ownValue = '';

        if (!isFiltersApplied) {
            ownValue = isProfileOwner ? '.private' : '.public';
        }

        return {
            title: t(`emptyList.nft${ownValue}.title`),
            description: t(`emptyList.nft.${isFiltersApplied || !!keywords ? 'textSearch' : 'text'}`),
            control: t(`emptyList.controls.${isProfileOwner ? 'createNft' : 'explore'}`),
        };
    }, [t, isFiltersApplied, isProfileOwner, keywords]);

    const handleControl = useCallback(() => {
        let route = '';
        if (isProfileOwner) {
            route = RouteE.COLLECTIBLE_CREATE;
        }
        if (!isProfileOwner) {
            route = RouteE.EXPLORE;
        }
        router.push(route);
    }, [router, isProfileOwner]);

    usePreventUrlChange({
        allowChangeRoute: true,
        callback: () => {
            setLaunchpadsFilter({});
        },
    });

    const emptyPlaceholder = useCallback(
        ({ itemsGridConfig }) => (
            <EmptyListPlaceholder itemsGridConfig={itemsGridConfig}>
                <Typography size="base" tag="div" variant="title" color="text">
                    {emptyList.title}
                </Typography>
                <Spacing margin="10px 0 30px">
                    <Typography size="sm" tag="div" color="gs200">
                        <span dangerouslySetInnerHTML={{ __html: emptyList.description }} />
                    </Typography>
                </Spacing>
                {(!isProfileOwner || (isProfileOwner && canCreate)) && (
                    <Button variant="white" onClick={handleControl}>
                        {emptyList.control}
                    </Button>
                )}
            </EmptyListPlaceholder>
        ),
        [canCreate, emptyList, handleControl, isProfileOwner]
    );

    useEffect(() => {
        clearFilters();
        return clearExplore;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getLayout = () => {
        if (isOceanTemplate) {
            return (
                <OProfilePage
                    profile={profile}
                    selectedTabId={selectedTabId}
                    setSelectedTabId={setSelectedTabId}
                    isFiltersApplied={isFiltersApplied}
                    clearFilters={clearFilters}
                    sideBarOpened={sideBarOpened}
                    toggleSideBar={toggleSideBar}
                    tabs={tabs}
                    emptyList={emptyList}
                    isProfileOwner={isProfileOwner}
                    canCreate={canCreate}
                    handleControl={handleControl}
                />
            );
        }

        return (
            <>
                <ProfileDetailsContainer profile={profile!} />
                <Tabs selectedTabId={selectedTabId} handleSelectTab={setSelectedTabId}>
                    <ExploreContentLayout>
                        <ExploreContentLayout.TopPanel>
                            <Visible view={['desktop', 'laptop']}>
                                <FilterButton
                                    showClearBtn={isFiltersApplied}
                                    onClear={clearFilters}
                                    opened={sideBarOpened}
                                    onToggle={toggleSideBar}
                                    disabled={selectedTabId !== 'items'}
                                />
                            </Visible>
                            <TabItemsWrapper>
                                <Tabs.TabItems tabItems={tabs} />
                            </TabItemsWrapper>
                        </ExploreContentLayout.TopPanel>
                        <ExploreContentLayout.Content>
                            <Tabs.Content tabId="items">
                                <ExploreContentLayout.SideBar opened={sideBarOpened}>
                                    <ExploreFilterContainer />
                                </ExploreContentLayout.SideBar>
                                <ExploreContentLayout.ExploreContent>
                                    <ExploreTopFiltersContainer />
                                    <NftsExploreContainer
                                        sideBarOpened={sideBarOpened}
                                        emptyPlaceholder={emptyPlaceholder}
                                    />
                                </ExploreContentLayout.ExploreContent>
                            </Tabs.Content>
                            <Tabs.Content tabId="collections">
                                <ExploreContentLayout.ExploreContent>
                                    <CollectionsSearchContainer />
                                    <CollectionsContainer />
                                </ExploreContentLayout.ExploreContent>
                            </Tabs.Content>
                            <Tabs.Content tabId="launchpads">
                                <ExploreContentLayout.ExploreContent>
                                    <ProfileLaunchpadsSearchContainer />
                                    <ProfileLaunchpadsContainer />
                                </ExploreContentLayout.ExploreContent>
                            </Tabs.Content>
                        </ExploreContentLayout.Content>
                    </ExploreContentLayout>
                </Tabs>
            </>
        );
    };

    return (
        <>
            <PageMeta title={`${meta.title}`} description={meta.description} ogImage={avatarAssets.retina} />
            <PageLayout fullWidth>{getLayout()}</PageLayout>
        </>
    );
};

export const getServerSideProps: GetServerSideProps = wrapper.getServerSideProps((store) => async (ctx) => {
    const { locale, query } = ctx;
    const { login } = query;

    const props = { ...(await serverSideTranslations(locale as string, PAGES_LOCALES)) };

    try {
        // @ts-ignore
        const { payload } = await store.dispatch(
            // @ts-ignore
            fetchPublicProfileThunk({
                login: login as string,
            })
        );

        if (payload.data.responseMeta?.error || !payload.data?.user) {
            throw new Error('Not found');
        }
        store.dispatch(launchpadsActions.clearItems());
        // @ts-ignore
        store.dispatch(actions.updateFilter({ ownerIds: [payload.data?.user.id] }));
        // @ts-ignore
        store.dispatch(collectionActions.updateFilter({ ownerIds: [payload.data?.user.id] }));
        // @ts-ignore
        store.dispatch(launchpadsActions.updateFilter({ ownerId: payload.data?.user.id }));
        // @ts-ignore
        const nftsRes = await store.dispatch(fetchNftsThunk());

        let usersToFetch = [];
        if (nftsRes?.list?.length) {
            usersToFetch = nftsRes?.list.map((item: NFTI) => item.author);
        }

        const collectionsIds = nftsRes?.list?.map((item: NFTI) => item.collectionId).filter(Boolean) ?? [];

        const res = await makeSSRApiRequest(
            {
                req: ctx.req,
                res: ctx.res,
            },
            ({ access }) => {
                const data = {
                    sorting: mapLaunchpadsSorting(),
                    filter: useCreateLaunchpadsFilterFieldsPayload({ ownerId: payload.data?.user.id }),
                    pager: {
                        page: 0,
                        limit: 20,
                    },
                };

                // @ts-ignore
                return LaunchpadAPI.getByFilter(
                    data,
                    access
                        ? {
                              Authorization: access,
                          }
                        : {}
                );
            }
        );

        store.dispatch(launchpadsActions.set(res.data));

        await Promise.all([
            // @ts-ignore
            store.dispatch(fetchUsersByWalletsThunk(usersToFetch)),
            // @ts-ignore
            store.dispatch(fetchCollectionsThunk()),
            // @ts-ignore
            await store.dispatch(fetchCollectionsByIdsThunk(collectionsIds)),
        ]);

        return {
            props,
        };
    } catch {
        return {
            props,
            notFound: true,
        };
    }
});

export default withUniverseVerification(ProfilePage);